<template>
  <button v-if="iconOnly" class="iconBtn" type="button" title="Add company" @click="open">
    <AppIcon name="add_company" :size="16" />
  </button>
  <button v-else class="btn secondary" type="button" @click="open">
    <span>Add company</span>
  </button>

  <Teleport to="body">
    <Modal v-if="isOpen" title="Add company" @close="close">
      <div class="addCandidateBody">
        <label class="addCandidateField">
          <span>Name</span>
          <input type="text" v-model="form.name" placeholder="Acme Inc." />
        </label>
        <label class="addCandidateField">
          <span>Domain</span>
          <input type="text" v-model="form.domain" placeholder="acme.com" @keydown.enter="submit" />
        </label>
        <p v-if="error" class="addCandidateError">{{ error }}</p>
      </div>
      <template #footer>
        <button class="btn secondary" @click="close">Cancel</button>
        <button class="btn primary" :disabled="busy || !valid" @click="submit">
          {{ busy ? 'Adding…' : 'Add' }}
        </button>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import Modal from './Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'

const props = defineProps<{
  playId: string
  refreshPlay: () => Promise<void>
  iconOnly?: boolean
}>()

const isOpen = ref(false)
const busy = ref(false)
const error = ref('')
const form = ref({ name: '', domain: '' })

const valid = computed(() => form.value.domain.trim().length > 0)

function open() {
  isOpen.value = true
  error.value = ''
  form.value = { name: '', domain: '' }
}

function close() {
  isOpen.value = false
  error.value = ''
}

async function submit() {
  if (!valid.value || busy.value) return
  busy.value = true
  error.value = ''
  const body: Record<string, string> = { kind: 'company', domain: form.value.domain.trim() }
  if (form.value.name.trim()) body.name = form.value.name.trim()
  try {
    await apiFetch(`/sales-plays/${props.playId}/add-candidate`, { method: 'POST', body })
    close()
    await props.refreshPlay()
  } catch (e: any) {
    error.value = e?.message || 'Failed to add company'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.addCandidateBody { display: flex; flex-direction: column; gap: var(--space-3); font-family: var(--font-sans); font-size: var(--fs-sm); }
.addCandidateField { display: flex; flex-direction: column; gap: var(--space-1); }
.addCandidateField > span { color: var(--color-text-secondary); font-size: var(--fs-xs); font-weight: var(--fw-semibold); text-transform: uppercase; letter-spacing: var(--tracking-wide); }
.addCandidateField input { padding: var(--space-2); border: 1px solid var(--color-border); border-radius: var(--radius-sm); font-family: var(--font-sans); font-size: var(--fs-sm); }
.addCandidateError { color: var(--color-danger, #b91c1c); font-size: var(--fs-sm); margin: 0; }

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}
.iconBtn:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
</style>
